import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { getDemoMessageByToken } from '../utils/demoSession';

function MessageViewer({ token }) {
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(null);

  const formatCountdown = (releaseDate) => {
    const now = new Date();
    const release = new Date(releaseDate);
    const diff = release - now;

    if (diff <= 0) return null;

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    return { days, hours, minutes, seconds };
  };

  const loadMessage = useCallback(async () => {
    try {
      const demoMessage = getDemoMessageByToken(token);
      if (demoMessage) {
        const releaseDate = demoMessage.release_date ? new Date(demoMessage.release_date) : null;
        const isReleased = !releaseDate || releaseDate <= new Date();
        const viewerMessage = isReleased
          ? {
              ...demoMessage,
              isReleased: true,
            }
          : {
              id: demoMessage.id,
              recipient_name: demoMessage.recipient_name,
              release_date: demoMessage.release_date,
              isReleased: false,
              message_text: null,
              message_type: demoMessage.message_type,
            };

        setMessage(viewerMessage);
        if (viewerMessage.isReleased === false && viewerMessage.release_date) {
          setCountdown(formatCountdown(viewerMessage.release_date));
        }
        setError(null);
        return;
      }

      const res = await axios.get(`/api/messages/view/${token}`);
      if (res.data.success) {
        setMessage(res.data.message);
        if (res.data.message.isReleased === false && res.data.message.release_date) {
          const cd = formatCountdown(res.data.message.release_date);
          setCountdown(cd);
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Laden der Nachricht');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadMessage();
  }, [loadMessage]);

  useEffect(() => {
    // Keep polling as long as the BACKEND says the message isn't released yet —
    // not just while our own countdown is still positive. The visitor's device
    // clock and the server's clock can disagree (e.g. different timezone
    // interpretation of the same naive "release_date" string), so a countdown
    // that locally reaches zero doesn't necessarily mean the backend agrees yet.
    // Re-checking with the server is what actually unlocks the message.
    if (!message || message.isReleased !== false || !message.release_date) return;

    const interval = setInterval(() => {
      const cd = formatCountdown(message.release_date);
      setCountdown(cd);
      if (!cd) {
        loadMessage();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [message, loadMessage]);

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1a1f35 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
      }}>
        <p style={{ color: '#a855f7', fontSize: '18px' }}>⏳ Nachricht wird geladen...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1a1f35 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
      }}>
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '12px',
          padding: '40px',
          textAlign: 'center',
          maxWidth: '400px'
        }}>
          <p style={{ color: '#ef4444', fontSize: '18px', marginBottom: '10px' }}>❌ Fehler</p>
          <p style={{ color: '#94a3b8' }}>{error}</p>
        </div>
      </div>
    );
  }

  if (!message) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1a1f35 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
      }}>
        <p style={{ color: '#94a3b8' }}>Nachricht nicht gefunden</p>
      </div>
    );
  }

  // Wenn Nachricht noch nicht freigegeben ist: Countdown anzeigen.
  // Wichtig: das Backend (isReleased) ist die Autoritaet, nicht der lokal
  // berechnete Countdown - dessen Uhr (Browser-Zeitzone) kann vom Server
  // abweichen. Ohne diese Trennung fiel die Ansicht bei einer Zeitzonen-
  // Differenz auf die "freigegeben"-Ansicht zurueck, obwohl message_text
  // vom Backend korrekt als null geliefert wurde - eine leere Karte statt
  // Countdown oder Inhalt.
  if (message.isReleased === false) {
    const cd = countdown || { days: 0, hours: 0, minutes: 0, seconds: 0 };
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1a1f35 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
      }}>
        <div style={{
          maxWidth: '600px',
          width: '100%'
        }}>
          {/* Header */}
          <div style={{
            textAlign: 'center',
            marginBottom: '40px'
          }}>
            <h1 style={{
              color: '#a855f7',
              fontSize: '32px',
              margin: 0,
              fontWeight: 300,
              letterSpacing: '2px'
            }}>
              TimeLock
            </h1>
            <p style={{
              color: '#64748b',
              fontSize: '13px',
              marginTop: '8px',
              letterSpacing: '1px'
            }}>
              DIGITALE VERMÄCHTNIS
            </p>
          </div>

          {/* Countdown Container */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(20, 28, 44, 0.8) 100%)',
            border: '2px solid rgba(168, 85, 247, 0.3)',
            borderRadius: '12px',
            padding: 'clamp(30px, 10vw, 80px) clamp(15px, 5vw, 40px)',
            position: 'relative',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(10px)',
            textAlign: 'center'
          }}>
            {/* Decorative lock icon */}
            <div style={{
              fontSize: '80px',
              marginBottom: '30px',
              animation: 'pulse 2s infinite'
            }}>
              🔐
            </div>

            {/* Message for recipient */}
            <p style={{
              color: '#a855f7',
              fontSize: '18px',
              fontWeight: 500,
              marginBottom: '10px',
              fontStyle: 'italic'
            }}>
              Liebe/r {message.recipient_name},
            </p>

            <p style={{
              color: '#94a3b8',
              fontSize: '14px',
              marginBottom: '50px',
              lineHeight: '1.6'
            }}>
              Eine persönliche Nachricht wurde für Sie hinterlegt.<br />
              Sie erhalten Zugang zum angegebenen Datum und zur angegebenen Uhrzeit.
            </p>

            {/* Countdown display */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
              gap: 'clamp(6px, 2vw, 15px)',
              marginBottom: '40px'
            }}>
              {/* Days */}
              <div style={{
                background: 'rgba(168, 85, 247, 0.1)',
                border: '1px solid rgba(168, 85, 247, 0.3)',
                borderRadius: '8px',
                padding: 'clamp(8px, 3vw, 20px) clamp(4px, 1.5vw, 10px)',
              }}>
                <div style={{
                  color: '#a855f7',
                  fontSize: 'clamp(16px, 6vw, 28px)',
                  fontWeight: 'bold',
                  fontVariantNumeric: 'tabular-nums'
                }}>
                  {String(cd.days).padStart(2, '0')}
                </div>
                <div style={{
                  color: '#64748b',
                  fontSize: '11px',
                  marginTop: '8px',
                  letterSpacing: '1px'
                }}>
                  TAGE
                </div>
              </div>

              {/* Hours */}
              <div style={{
                background: 'rgba(168, 85, 247, 0.1)',
                border: '1px solid rgba(168, 85, 247, 0.3)',
                borderRadius: '8px',
                padding: 'clamp(8px, 3vw, 20px) clamp(4px, 1.5vw, 10px)',
              }}>
                <div style={{
                  color: '#a855f7',
                  fontSize: 'clamp(16px, 6vw, 28px)',
                  fontWeight: 'bold',
                  fontVariantNumeric: 'tabular-nums'
                }}>
                  {String(cd.hours).padStart(2, '0')}
                </div>
                <div style={{
                  color: '#64748b',
                  fontSize: '11px',
                  marginTop: '8px',
                  letterSpacing: '1px'
                }}>
                  STUNDEN
                </div>
              </div>

              {/* Minutes */}
              <div style={{
                background: 'rgba(168, 85, 247, 0.1)',
                border: '1px solid rgba(168, 85, 247, 0.3)',
                borderRadius: '8px',
                padding: 'clamp(8px, 3vw, 20px) clamp(4px, 1.5vw, 10px)',
              }}>
                <div style={{
                  color: '#a855f7',
                  fontSize: 'clamp(16px, 6vw, 28px)',
                  fontWeight: 'bold',
                  fontVariantNumeric: 'tabular-nums'
                }}>
                  {String(cd.minutes).padStart(2, '0')}
                </div>
                <div style={{
                  color: '#64748b',
                  fontSize: '11px',
                  marginTop: '8px',
                  letterSpacing: '1px'
                }}>
                  MINUTEN
                </div>
              </div>

              {/* Seconds */}
              <div style={{
                background: 'rgba(168, 85, 247, 0.1)',
                border: '1px solid rgba(168, 85, 247, 0.3)',
                borderRadius: '8px',
                padding: 'clamp(8px, 3vw, 20px) clamp(4px, 1.5vw, 10px)',
              }}>
                <div style={{
                  color: '#a855f7',
                  fontSize: 'clamp(16px, 6vw, 28px)',
                  fontWeight: 'bold',
                  fontVariantNumeric: 'tabular-nums'
                }}>
                  {String(cd.seconds).padStart(2, '0')}
                </div>
                <div style={{
                  color: '#64748b',
                  fontSize: '11px',
                  marginTop: '8px',
                  letterSpacing: '1px'
                }}>
                  SEKUNDEN
                </div>
              </div>
            </div>

            {/* Release date info */}
            <div style={{
              color: '#94a3b8',
              fontSize: '12px',
              marginTop: '30px'
            }}>
              <p style={{ margin: '0 0 5px 0' }}>Freigabedatum:</p>
              <p style={{ margin: 0, color: '#a855f7', fontSize: '14px' }}>
                {new Date(message.release_date).toLocaleDateString('de-DE', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
          </div>

          {/* Security notice */}
          <div style={{
            marginTop: '40px',
            padding: '16px',
            background: 'rgba(52, 211, 153, 0.1)',
            border: '1px solid rgba(52, 211, 153, 0.3)',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <p style={{
              color: '#86efac',
              fontSize: '12px',
              margin: 0,
              lineHeight: '1.6'
            }}>
              🔒 Diese Nachricht ist sicher verschlüsselt.<br />
              Sie wird automatisch freigeschaltet, sobald das Freigabedatum erreicht ist.
            </p>
          </div>

          {/* TimeLock footer */}
          <div style={{
            marginTop: '60px',
            textAlign: 'center',
            color: '#64748b',
            fontSize: '12px'
          }}>
            <p>
              Powered by <span style={{ color: '#a855f7', fontWeight: 600 }}>TimeLock</span> - 
              Dein digitales Vermächtnis
            </p>
          </div>
        </div>

        <style>{`
          @keyframes pulse {
            0% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.05); opacity: 0.8; }
            100% { transform: scale(1); opacity: 1; }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1a1f35 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Caveat:wght@700&display=swap');
        .handwritten-text {
          font-family: 'Caveat', cursive;
          font-size: 20px;
          font-weight: 700;
          letter-spacing: 0.5px;
          line-height: 2;
          color: #1a1a1a;
        }
      `}</style>
      <div style={{
        maxWidth: '700px',
        width: '100%'
      }}>
        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '40px'
        }}>
          <h1 style={{
            color: '#a855f7',
            fontSize: '32px',
            margin: 0,
            fontWeight: 300,
            letterSpacing: '2px'
          }}>
            TimeLock
          </h1>
          <p style={{
            color: '#64748b',
            fontSize: '13px',
            marginTop: '8px',
            letterSpacing: '1px'
          }}>
            DIGITALE VERMÄCHTNIS
          </p>
        </div>

        {/* Letter Paper Container */}
        <div style={{
          background: 'linear-gradient(135deg, #f5f1e8 0%, #fffbf5 100%)',
          border: '1px solid rgba(200, 180, 150, 0.3)',
          borderRadius: '4px',
          padding: '60px 50px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.6)',
          position: 'relative',
          minHeight: '400px'
        }}>
          {/* Paper texture overlay */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.03,
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Crect width=\'100\' height=\'100\' fill=\'%23000\'/%3E%3C/svg%3E")',
            pointerEvents: 'none'
          }} />

          {/* Recipient greeting */}
          <p className="handwritten-text" style={{
            color: '#2d2d2d',
            fontSize: '22px',
            fontStyle: 'italic',
            marginTop: 0,
            marginBottom: message.release_date ? '4px' : '30px'
          }}>
            Liebe/r {message.recipient_name},
          </p>
          {message.release_date && (
            <p style={{
              color: '#8b5a3c',
              fontSize: '13px',
              fontStyle: 'italic',
              marginTop: 0,
              marginBottom: '30px'
            }}>
              📅 Freigabe: {new Date(message.release_date).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })}
            </p>
          )}

          {/* Message content */}
          {message.message_type === 'video' ? (
            <div style={{ marginBottom: '40px' }}>
              <div style={{ marginBottom: '24px' }}>
                <p style={{
                  color: '#666',
                  fontSize: '12px',
                  marginBottom: '10px',
                  fontWeight: 500
                }}>
                  🎥 Videobotschaft
                </p>
                <video
                  controls
                  poster={message.message_image || undefined}
                  style={{
                    width: '100%',
                    borderRadius: '8px',
                    border: '1px solid #ccc',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    background: '#0f172a',
                  }}
                >
                  <source src={message.message_video} type="video/mp4" />
                </video>
              </div>

              {message.message_text && (
                <div className="handwritten-text" style={{
                  color: '#1a1a1a',
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  lineHeight: '1.8'
                }}>
                  {message.message_text}
                </div>
              )}
            </div>
          ) : message.message_image ? (
            <div style={{ marginBottom: '40px' }}>
              {/* Original image */}
              <div style={{
                marginBottom: '30px',
                textAlign: 'center'
              }}>
                <p style={{
                  color: '#666',
                  fontSize: '12px',
                  marginBottom: '10px',
                  fontWeight: 500
                }}>
                  📄 Original Handschrift:
                </p>
                <img 
                  src={message.message_image} 
                  alt="Handwritten letter"
                  style={{
                    maxWidth: '100%',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                  }}
                />
              </div>

              {/* Digitized text */}
              {message.message_text && (
                <div style={{
                  borderTop: '1px solid #d0c5b0',
                  paddingTop: '30px',
                  marginTop: '30px'
                }}>
                  <p style={{
                    color: '#666',
                    fontSize: '12px',
                    marginBottom: '15px',
                    fontWeight: 500
                  }}>
                    💻 Digitale Fassung (OCR):
                  </p>
                  <div className="handwritten-text" style={{
                    color: '#1a1a1a',
                    minHeight: '100px',
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                    lineHeight: '1.8'
                  }}>
                    {message.message_text}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="handwritten-text" style={{
              color: '#1a1a1a',
              fontSize: '20px',
              lineHeight: '2',
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
              marginBottom: '40px',
              minHeight: '150px',
              letterSpacing: '0.5px'
            }}>
              {message.message_text}
            </div>
          )}


        </div>

        {/* Footer */}
        <div style={{
          marginTop: '60px',
          textAlign: 'center',
          color: '#64748b',
          fontSize: '12px'
        }}>
          <p>
            Powered by <span style={{ color: '#a855f7', fontWeight: 600 }}>TimeLock</span> - 
            Dein digitales Vermächtnis
          </p>
        </div>
      </div>
    </div>
  );
}

export default MessageViewer;
